"use client";

import { useEffect, useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import Link from 'next/link';
import './inventory-intelligence.css';

/* All styling now lives in inventory-intelligence.css (no styling in this file).
   Images public/assets/images/ se serve hoti hain.
   Industry cards ke liye files ke naam EXACT ye hone chahiye (koi space nahi):
   - Retail-&-CPG.jpg
   - Manufacturing.jpg
   - Pharma-&-Healthcare.jpg
   - Auto-&-Industrial.jpg
   - Utilities-&-Energy.jpg
   - Professional-Services.jpg
   Hero background: CSS mein (".ii-hero-bg-image").
   Final CTA background: Do-More-Together..jpg (naam mein 2 dots hain). */

/* ---------- Scroll reveal (bottom to top) ---------- */

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const items = root.querySelectorAll<HTMLElement>('[data-reveal]');

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return ref;
}

const delay = (i: number, step = 80, max = 400): CSSProperties =>
  ({ '--ii-delay': `${Math.min(i * step, max)}ms` } as CSSProperties);

type IconKey =
  | 'box'
  | 'trend'
  | 'pin'
  | 'refresh'
  | 'cart'
  | 'shield'
  | 'bulb'
  | 'search'
  | 'network'
  | 'alert'
  | 'chat'
  | 'file'
  | 'chart'
  | 'download';

function Icon({ name }: { name: IconKey }) {
  const paths: Record<IconKey, ReactNode> = {
    box: <><path d="m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Z" /><path d="m4 7.5 8 4.5 8-4.5M12 12v9" /></>,
    trend: <><path d="M3 19h18M5 16l5-5 3 3 6-8" /><path d="M14 6h5v5" /></>,
    pin: <><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></>,
    refresh: <><path d="M20 7v5h-5M4 17v-5h5" /><path d="M5.5 9A7 7 0 0 1 18 6l2 6M4 12l2 6a7 7 0 0 0 12.5-3" /></>,
    cart: <><path d="M3 4h2l2.2 11.2a2 2 0 0 0 2 1.6h8.9a2 2 0 0 0 1.9-1.4L22 8H6" /><circle cx="10" cy="20" r="1" /><circle cx="18" cy="20" r="1" /></>,
    shield: <><path d="M12 3 5 6v5c0 4.5 3 8 7 10 4-2 7-5.5 7-10V6z" /><path d="m9 12 2 2 4-4" /></>,
    bulb: <><path d="M9 18h6M10 22h4M8.5 14.5A7 7 0 1 1 15.5 14.5c-.8.7-1.2 1.5-1.3 2.5h-4.4c-.1-1-.5-1.8-1.3-2.5Z" /></>,
    search: <><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5M8 10.5h5M10.5 8v5" /></>,
    network: <><circle cx="12" cy="5" r="2.5" /><circle cx="5" cy="19" r="2.5" /><circle cx="19" cy="19" r="2.5" /><path d="M12 7.5v4M12 11.5l-6 5.5M12 11.5l6 5.5" /></>,
    alert: <><path d="M12 3 2.8 20h18.4L12 3Z" /><path d="M12 9v5M12 17h.01" /></>,
    chat: <><path d="M4 5.5A3.5 3.5 0 0 1 7.5 2h9A3.5 3.5 0 0 1 20 5.5v8a3.5 3.5 0 0 1-3.5 3.5H10l-6 4v-6.5Z" /><path d="M8 9h8M8 13h5" /></>,
    file: <><path d="M6 3h8l5 5v13H6z" /><path d="M14 3v5h5M9 13h7M9 17h7" /></>,
    chart: <><path d="M4 20V4M4 20h17M8 16v-4M12 16V8M16 16v-6" /></>,
    download: <><path d="M12 3v12m-5-5 5 5 5-5M4 20h16" /></>,
  };

  return (
    <svg
      className="ii-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name]}
    </svg>
  );
}

function Arrow() {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path d="M14 5l7 7m0 0l-7 7m7-7H3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
    </svg>
  );
}

/* ============================== DATA ============================== */

const sideNav = [
  'Inventory Intelligence',
  'Cash & Working Capital',
  'Margin & Profitability',
  'Spend & Vendor',
  'Asset & Spare Intelligence',
  'Customer & Revenue',
  'People Intelligence',
  'CXO Intelligence',
];

const benefits: { icon: IconKey; title: string; text: string }[] = [
  { icon: 'box', title: 'Lower Inventory Cost', text: 'Reduce working capital and carrying costs' },
  { icon: 'shield', title: 'Higher Service Level', text: 'Right product, right time, right place' },
  { icon: 'bulb', title: 'Better Forecast Accuracy', text: 'AI-driven demand sensing and planning' },
  { icon: 'search', title: 'End-to-End Visibility', text: 'From supplier to plant to DC to store' },
  { icon: 'trend', title: 'Faster, Smarter Decisions', text: 'Real-time insights and scenario planning' },
];

const questions: { icon: IconKey; tone: string; text: string }[] = [
  { icon: 'box', tone: 'amber', text: 'How much inventory do we really need?' },
  { icon: 'trend', tone: 'green', text: 'Where will demand increase or drop?' },
  { icon: 'pin', tone: 'blue', text: 'Which locations are at risk of stockout?' },
  { icon: 'refresh', tone: 'purple', text: 'How do we reduce slow-moving and obsolete stock?' },
  { icon: 'cart', tone: 'rose', text: 'How do we improve OTIF and customer service level?' },
];

/* href = industry page. Slugs must match the routes used in the header Industries dropdown. */
const industries = [
  { title: 'Retail & CPG', desc: 'Assortment planning, store replenishment, promotions', image: '/assets/images/Retail-&-CPG.jpg', href: '/industries/fmcg-retail' },
  { title: 'Manufacturing', desc: 'MPS, MRP, plant and spare parts inventory', image: '/assets/images/Manufacturing.jpg', href: '/industries/manufacturing' },
  { title: 'Pharma & Healthcare', desc: 'FEFO, batch tracking, regulatory compliance', image: '/assets/images/Pharma-&-Healthcare.jpg', href: '/industries/pharma-lifescience' },
  { title: 'Auto & Industrial', desc: 'Multi-echelon planning, dealer inventory', image: '/assets/images/Auto-&-Industrial.jpg', href: '/industries/manufacturing' },
  { title: 'Utilities & Energy', desc: 'Spares and materials planning', image: '/assets/images/Utilities-&-Energy.jpg', href: '/industries/energy-utilities' },
  { title: 'Professional Services', desc: 'Project and service inventory, kits and spares', image: '/assets/images/Professional-Services.jpg', href: '/industries/professional-services' },
];

const useCases: { icon: IconKey; title: string; desc: string; tone: string }[] = [
  { icon: 'chart', title: 'AI Demand Forecasting', desc: 'Combine historical data, promotions, and external signals.', tone: 'blue' },
  { icon: 'network', title: 'Replenishment Optimization', desc: 'Multi-echelon, location-wise, service-level based.', tone: 'green' },
  { icon: 'alert', title: 'Slow-Moving & Obsolete Detection', desc: 'Identify, classify and recommend actions.', tone: 'amber' },
  { icon: 'box', title: 'Intelligent Allocation', desc: 'Optimise allocation across plants, DCs and stores.', tone: 'purple' },
  { icon: 'alert', title: 'Exception Management', desc: 'Proactive alerts for stockouts, excess and delays.', tone: 'rose' },
  { icon: 'chat', title: 'Conversational Inventory Assistant', desc: 'Ask questions in natural language about inventory.', tone: 'cyan' },
];

const problems = [
  'Inaccurate demand forecasting',
  'High safety stock and excess inventory',
  'Frequent stockouts and lost sales',
  'Slow-moving and obsolete inventory',
  'Poor visibility across locations',
];

const resources: { title: string; type: string; tone: string }[] = [
  { title: 'Inventory Assessment Template', type: 'Excel', tone: 'green' },
  { title: 'Inventory Analytics Dashboard', type: 'Power BI', tone: 'amber' },
  { title: 'Inventory Optimization Playbook', type: 'PDF', tone: 'rose' },
  { title: 'Case Study: CPG Retail', type: 'PDF', tone: 'rose' },
];

const journey = [
  { title: 'Digital Day', desc: 'Align on business questions and opportunities' },
  { title: 'Discovery & Assessment', desc: 'Data, current state and opportunity analysis' },
  { title: 'Solution Demo', desc: 'Showcase tailored use cases with your data' },
  { title: 'Pilot', desc: 'Prove value in a focused area (4–8 weeks)' },
  { title: 'Scale', desc: 'Enterprise rollout and continuous improvement' },
];

const finalValues = ['MORE KNOWLEDGE', 'MORE CAPABILITY', 'MORE OPPORTUNITY', 'MORE VALUE'];

/* ============================== PAGE ============================== */

export default function InventoryIntelligencePage() {
  const ref = useScrollReveal();

  return (
    <div className="ii-page" ref={ref}>
      <main>
        {/* ============================= HERO ============================= */}
        <section className="ii-hero" data-purpose="hero-section" id="top">
          <div className="ii-hero-bg-image"></div>
          <div className="ii-hero-bg-overlay"></div>
          <div className="ii-hero-grid"></div>
          <div className="ii-hero-glow ii-hero-glow--left"></div>
          <div className="ii-hero-glow ii-hero-glow--right"></div>

          <div className="op-container op-container--left ii-hero-inner">
            <div className="ii-hero-layout">
              <div className="ii-hero-content">

                <h1 className="ii-hero-title" data-reveal style={delay(0)}>
                  Inventory
                  <span className="ii-hero-title-accent">Intelligence</span>
                </h1>

                <p className="ii-hero-subtitle" data-reveal style={delay(1, 130)}>Right inventory. Right place. Lower cost. Higher service.</p>

                <p className="ii-hero-text" data-reveal style={delay(2, 130)}>
                  Use your data, business rules and AI to improve forecasting, planning, replenishment
                  and execution across your supply chain — from plant to warehouse to store.
                </p>

                <div className="ii-hero-actions" data-reveal style={delay(3, 130)}>
                  <Link href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary">
                    Book a Discovery Session <span>→</span>
                  </Link>
                  <Link href="#benefits" className="fp-buttons fp-hero-outline-btn">
                    See it in Action <span>↗</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================ BENEFITS ============================ */}
        <section className="op-section op-section--white ii-benefits-section" id="benefits" data-purpose="benefits" aria-labelledby="ii-benefits-title">
          <div className="op-container">
            <div className="ii-benefits-heading" data-reveal>
              <span className="ii-benefits-eyebrow">INVENTORY INTELLIGENCE</span>
              <h2 id="ii-benefits-title">Transform Inventory into a Strategic Advantage</h2>
              <p>Unlock measurable business impact through smarter, data-driven inventory management.</p>
            </div>
            <div className="ii-benefits-grid">
              {benefits.map((b, i) => (
                <article className={`ii-benefit-card ii-benefit-card--${i + 1}`} key={b.title} data-reveal style={delay(i, 90)}>
                  <span className="ii-benefit-number">{String(i + 1).padStart(2, '0')}</span>
                  <div className="ii-benefit-copy">
                    <h3>{b.title}</h3>
                    <p>{b.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== KEY BUSINESS QUESTIONS ===================== */}
        <section className="op-section op-section--slate" data-purpose="business-questions">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <div className="sec-badge">Real Decisions</div>
              <h2 className="sec-title">Key Business Questions</h2>
              <p className="sec-sub">Turn your inventory questions into intelligent decisions.</p>
            </div>

            <div className="q-grid">
              {questions.map((q, i) => (
                <div className="q-card" key={q.text} data-reveal style={delay(i, 90)}>
                  <span className={`q-icon tone-${q.tone}`}>
                    <Icon name={q.icon} />
                  </span>
                  <p className="q-text">{q.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== INDUSTRY SCENARIOS ======================== */}
        <section className="op-section op-section--white" data-purpose="industry-scenarios">
          <div className="op-container">
            <div className="sec-head" data-reveal>
              <div className="sec-badge">Industries</div>
              <h2 className="sec-title">Industry Scenarios</h2>
              <p className="sec-sub">Proven across industries with real-world templates and accelerators.</p>
            </div>

            <div className="ind-grid">
              {industries.map((item, i) => (
                <Link className="ind-card" href={item.href} key={item.title} data-reveal style={delay(i % 3, 110)}>
                  <div className="ind-image">
                    <img src={item.image} alt="" loading="lazy" />
                  </div>
                  <div className="ind-body">
                    <h3 className="ind-title">{item.title}</h3>
                    <p className="ind-text">{item.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ======================== AI-ENABLED USE CASES ======================== */}
        <section className="op-section op-section--slate" data-purpose="use-cases" id="use-cases">
          <div className="op-container op-container--left">
            <div className="sec-head coverage-section-head" data-reveal>
              <div className="sec-badge">Applied AI</div>
              <h2 className="sec-title">AI-Enabled Use Cases</h2>
              <p className="sec-sub">From insight to action with applied AI.</p>
            </div>

            <div className="coverage-layout">
              <div className="coverage-main">
                <div className="coverage-timeline">
                  {useCases.map((item, i) => (
                    <article className="coverage-timeline-item" key={item.title} data-reveal style={delay(i, 80, 400)}>
                      <div className="coverage-timeline-marker" aria-hidden="true">
                        <span className="coverage-timeline-number">{String(i + 1).padStart(2, '0')}</span>
                        <span className="coverage-timeline-dot"></span>
                      </div>
                      <div className="coverage-timeline-content">
                        <h3 className="coverage-title">{item.title}</h3>
                        <p className="coverage-desc">{item.desc}</p>
                      </div>
                      <span className="coverage-timeline-link">Use Case <span aria-hidden="true">→</span></span>
                    </article>
                  ))}
                </div>
              </div>
              <div className="coverage-visual" data-reveal style={delay(1, 150)}>
                <img src="/assets/images/AI-Enabled-Use-Cases.jpg" alt="Inventory insights and applied AI visualization" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* ================= 52 PROBLEMS + RESOURCES ================= */}
        <section className="op-section op-section--white ii-pr-section" data-purpose="problems-and-resources" id="resources">
          <div className="op-container">
            <div className="ii-pr-layout">
              <div className="ii-pr-problems">
                <div className="ii-pr-section-title" data-reveal>
                  <h2>52 Inventory Problems We Solve</h2>
                  <p>Real problems. Practical solutions.</p>
                </div>
                <div className="ii-pr-card ii-pr-problem-card" data-reveal style={delay(1, 120)}>
                  <ol className="ii-pr-problem-list">
                    {problems.map((p, i) => (
                      <li key={p}>
                        <b>{String(i + 1).padStart(2, "0")}</b>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <div className="ii-pr-resources">
                <div className="ii-pr-section-title" data-reveal style={delay(1, 100)}>
                  <h2>Resources &amp; Downloads</h2>
                  <p>Templates, frameworks and case examples.</p>
                </div>
                <div className="ii-pr-card ii-pr-resource-card" data-reveal style={delay(2, 120)}>
                  <div className="ii-pr-resource-list">
                    {resources.map((r) => (
                      <a className="ii-pr-resource" href="/tellusyourproblem#tell-details" key={r.title}>
                        <span className={`ii-pr-file-icon ii-pr-file-${r.tone}`}>
                          <Icon name="file" />
                        </span>
                        <span className="ii-pr-resource-copy">
                          <strong>{r.title}</strong>
                          <small>{r.type}</small>
                        </span>
                        <span className="ii-pr-download" aria-hidden="true">
                          <Icon name="download" />
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="op-container ii-pr-cta-shell">
            <div className="ii-pr-cta-row" data-reveal>
              <Link href="/52problem#all-problems" className="ii-pr-view-link">
                View All 52 Inventory Problems <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </section>

        {/* ======================= ENGAGEMENT JOURNEY ======================= */}
        <section className="op-section op-section--slate" data-purpose="engagement-journey">
          <div className="op-container">
            <div className="sec-head sec-head--center" data-reveal>
              <div className="sec-badge">The Journey Roadmap</div>
              <h2 className="sec-title">Our Engagement Journey</h2>
              <p className="sec-sub sec-sub--base">From understanding to measurable outcomes.</p>
            </div>

            <div className="flow-scroll ii-journey-scroll">
              <div className="flow-track ii-journey-track">
                {journey.map((step, i) => (
                  <div className="flow-step ii-journey-step" key={step.title} data-reveal style={delay(i, 110, 440)}>
                    <span className="flow-num ii-journey-num">{i + 1}</span>
                    <div className="ii-journey-content">
                      <h3 className="flow-title ii-journey-title">{step.title}</h3>
                      <p className="flow-desc ii-journey-desc">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ========================= FINAL CTA ================================ */}
        <section className="ii-cta" data-purpose="final-call-to-action" id="contact">
          <img className="ii-cta-bg" src="/assets/images/Do-More-Together..jpg" alt="" aria-hidden="true" />
          <div className="ii-cta-copy">
            <span className="ii-eyebrow" data-reveal>SMARTER INVENTORY. STRONGER SUPPLY CHAINS.</span>
            <h2 data-reveal style={delay(1, 120)}>Do More. Together.</h2>
            <p data-reveal style={delay(2, 120)}>
              Real business impact. Let’s explore how Inventory Intelligence can work for your
              business.
            </p>
            <div className="ii-cta-actions" data-reveal style={delay(3, 120)}>
              <Link href="/tellusyourproblem#tell-details" className="fp-buttons fp-btn-primary">
                Talk to Our Team <span>→</span>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}